'use client';

import React, { useEffect, useState } from 'react';
import Close from '@carbon/icons-react/es/Close.js';
import ArrowRight from '@carbon/icons-react/es/ArrowRight.js';
import { getBanners, type BannerItem } from '../../apis/BannerAPIs';
import styles from './CommonBanner.module.scss';

const COOKIE_NAME = 'developer_common_banner';
const COOKIE_VALUE = 'NOBANNER';
const EXPIRATION_HOURS = 12;

function getCookieValue(name: string): string | null {
  if (typeof document === 'undefined') return null;
  // Use split-based parsing instead of a dynamically constructed RegExp to
  // eliminate any ReDoS risk from untrusted cookie name values.
  for (const part of document.cookie.split(';')) {
    const trimmed = part.trimStart();
    const eqIdx = trimmed.indexOf('=');
    if (eqIdx === -1) continue;
    if (trimmed.slice(0, eqIdx) === name) {
      return decodeURIComponent(trimmed.slice(eqIdx + 1));
    }
  }
  return null;
}

function setDismissCookie(bannerId: string): void {
  const expires = new Date(Date.now() + EXPIRATION_HOURS * 60 * 60 * 1000).toUTCString();
  document.cookie = `${COOKIE_NAME}_${bannerId}=${COOKIE_VALUE}; expires=${expires}; path=/; SameSite=Strict; Secure`;
}

const CommonBanner: React.FC = () => {
  const [bannerData, setBannerData] = useState<BannerItem | null>(null);

  useEffect(() => {
    let cancelled = false;

    getBanners().then((response) => {
      if (cancelled) return;

      const item = response?.results?.[0];
      if (!item?.banner_id) return;

      const cookieKey = `${COOKIE_NAME}_${item.banner_id}`;
      if (getCookieValue(cookieKey)) return; // already dismissed

      setBannerData(item);
      document.documentElement.style.setProperty('--sub-banner-height', '60px');
    });

    return () => {
      cancelled = true;
    };
  }, []);

  const handleDismiss = () => {
    if (!bannerData) return;
    setDismissCookie(bannerData.banner_id);
    document.documentElement.style.setProperty('--sub-banner-height', '0px');
    setBannerData(null);
  };

  if (!bannerData) return null;

  return (
    <div
      className={styles.commonBanner}
      role="banner"
      aria-label="Site announcement"
      data-testid="common-banner"
    >
      <a href={bannerData.button_url} className={styles.bannerLink}>
        <span className={styles.bannerText}>{bannerData.banner_text}</span>
        <span className={styles.bannerButtonText}>
          {bannerData.button_text}
          <ArrowRight size={16} aria-hidden="true" className={styles.bannerArrow} />
        </span>
      </a>

      <button
        type="button"
        className={styles.closeButton}
        aria-label="Dismiss banner"
        onClick={handleDismiss}
        data-testid="banner-close"
      >
        <Close size={20} aria-hidden="true" />
      </button>
    </div>
  );
};

export default CommonBanner;
